/**
 * Calculator Reset Button Component
 * Provides a confirmation dialog before resetting calculator to defaults
 */

import React from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

export interface CalculatorResetButtonProps {
  onReset: () => void;
  isResetting?: boolean;
  variant?: 'default' | 'icon' | 'compact';
  className?: string;
}

export const CalculatorResetButton: React.FC<CalculatorResetButtonProps> = ({
  onReset,
  isResetting = false,
  variant = 'default',
  className,
}) => {
  const handleReset = () => {
    onReset();
  };

  const buttonContent = () => {
    switch (variant) {
      case 'icon':
        return (
          <Button
            variant="ghost"
            size="icon"
            disabled={isResetting}
            className={cn('h-9 w-9', className)}
          >
            <RotateCcw className={cn('h-4 w-4', isResetting && 'animate-spin')} />
            <span className="sr-only">Reset Calculator</span>
          </Button>
        );
      case 'compact':
        return (
          <Button
            variant="ghost"
            size="sm"
            disabled={isResetting}
            className={cn('gap-1.5', className)}
          >
            <RotateCcw className={cn('h-3.5 w-3.5', isResetting && 'animate-spin')} />
            <span className="text-xs">Reset</span>
          </Button>
        );
      default:
        return (
          <Button
            variant="outline"
            size="sm"
            disabled={isResetting}
            className={cn('gap-2', className)}
          >
            <RotateCcw className={cn('h-4 w-4', isResetting && 'animate-spin')} />
            Reset Calculator
          </Button>
        );
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {buttonContent()}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset Calculator?</AlertDialogTitle>
          <AlertDialogDescription>
            This will clear all your current values and reset the calculator to its default state.
            Any saved draft data will also be cleared. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleReset} disabled={isResetting}>
            {isResetting ? 'Resetting...' : 'Reset'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CalculatorResetButton;
